import React, { useState, useEffect } from "react";
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Alert, 
  Typography, 
  Space, 
  Tag, 
  Table, 
  Tabs,
  Progress,
  Button,
  Modal,
  Descriptions,
  Skeleton
} from "antd";
import { 
  UserOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BarChartOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  LineChartOutlined
} from "@ant-design/icons";
import { ANALYTICS_API_URL } from "../../../constants/config";
import { getAndSanitize } from "../../../shared/utils/sanitization";
import { useAnalytics } from "../../../contexts/AnalyticsContext";
import { UsernameTrendChart } from "../components/charts/UsernameTrendChart";
import { RejectedAmountTrendChart } from "../components/charts/RejectedAmountTrendChart";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface UsernameAnalysis {
  original: string;
  parsed: string;
  status: string;
  count: number;
  similarity: number;
  createdAt: string;
}

interface AmountAnalysis {
  original: string;
  parsed: number | null;
  status: string;
  count: number;
  type: string;
  isNumeric: boolean;
  createdAt: string;
}

interface ParseAnalyticsData {
  usernameAnalysis: {
    approved: UsernameAnalysis[];
    rejected: UsernameAnalysis[];
    statistics: {
      totalApproved: number;
      totalRejected: number;
      averageAccuracy: number;
      commonMistakes: Array<{
        original: string;
        parsed: string;
        count: number;
      }>;
    };
  };
  amountAnalysis: {
    approved: AmountAnalysis[];
    rejected: AmountAnalysis[];
    statistics: {
      totalApproved: number;
      totalRejected: number;
      averageAccuracy: number;
      nonNumericInputs: number;
      commonMistakes: Array<{
        original: string;
        parsed: number | null;
        count: number;
      }>;
    };
  };
}

interface ParseAnalyticsProps {
  loading?: boolean;
}

export const ParseAnalytics: React.FC<ParseAnalyticsProps> = ({ loading: propLoading }) => {
  const [data, setData] = useState<ParseAnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("usernames");
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedMistake, setSelectedMistake] = useState<any>(null);
  const { filters } = useAnalytics();

  const fetchParseAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getAndSanitize("token");
      if (!token) {
        throw new Error("Token no disponible");
      }

      const params = new URLSearchParams();
      if (filters.dateRange.start && filters.dateRange.end) {
        params.append("dateFrom", filters.dateRange.start);
        params.append("dateTo", filters.dateRange.end);
      }

      const response = await fetch(`${ANALYTICS_API_URL}/api/analytics/parse-analytics?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setData(result.data);
      } else {
        throw new Error(result.message || "Error al obtener datos");
      }
    } catch (err: any) {
      console.error("Error fetching parse analytics:", err);
      setError(err.message || "Error al cargar análisis de parseo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParseAnalytics();
  }, [filters]);

  const handleExport = async (format: 'json' | 'csv') => {
    try {
      const token = getAndSanitize("token");
      if (!token) return;

      const params = new URLSearchParams({ format });
      if (filters.dateRange.start && filters.dateRange.end) {
        params.append("dateFrom", filters.dateRange.start);
        params.append("dateTo", filters.dateRange.end);
      }

      const response = await fetch(`${ANALYTICS_API_URL}/api/analytics/parse-analytics/export?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `parse-analytics-${dayjs().format('YYYY-MM-DD')}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error exporting data:", error);
    }
  };

  const showMistakeDetail = (mistake: any, type: 'username' | 'amount') => {
    setSelectedMistake({ ...mistake, type });
    setDetailModalVisible(true);
  };

  const usernameColumns = [
    {
      title: 'Original',
      dataIndex: 'original',
      key: 'original',
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: 'Parseado',
      dataIndex: 'parsed',
      key: 'parsed',
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'APPROVED' ? 'green' : 'red'} icon={status === 'APPROVED' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {status === 'APPROVED' ? 'Aprobado' : 'Rechazado'}
        </Tag>
      ),
    },
    {
      title: 'Similitud',
      dataIndex: 'similarity',
      key: 'similarity',
      render: (similarity: number) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Progress 
            percent={Math.round(similarity * 100)} 
            size="small" 
            status={similarity >= 0.8 ? 'success' : similarity >= 0.6 ? 'normal' : 'exception'}
            style={{ minWidth: 80 }}
          />
          <Text>{Math.round(similarity * 100)}%</Text>
        </div>
      ),
    },
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
  ];

  const amountColumns = [
    {
      title: 'Original',
      dataIndex: 'original',
      key: 'original',
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: 'Parseado',
      dataIndex: 'parsed',
      key: 'parsed',
      render: (amount: number | null) => (
        amount !== null ? <Text>${amount.toFixed(2)}</Text> : <Text type="secondary">No parseado</Text>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={type === 'LOAD' ? 'blue' : 'orange'}>
          {type === 'LOAD' ? 'Carga' : 'Retiro'}
        </Tag>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'APPROVED' ? 'green' : 'red'} icon={status === 'APPROVED' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {status === 'APPROVED' ? 'Aprobado' : 'Rechazado'}
        </Tag>
      ),
    },
    {
      title: 'Es Numérico',
      dataIndex: 'isNumeric',
      key: 'isNumeric',
      render: (isNumeric: boolean) => (
        <Tag color={isNumeric ? 'green' : 'red'} icon={isNumeric ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {isNumeric ? 'Sí' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
  ];

  if (loading || propLoading) {
    return (
      <div style={{ padding: '16px' }}>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Skeleton active paragraph={{ rows: 2 }} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Skeleton active paragraph={{ rows: 2 }} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Skeleton active paragraph={{ rows: 2 }} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Skeleton active paragraph={{ rows: 2 }} />
            </Card>
          </Col>
        </Row>
        <Card>
          <Skeleton active paragraph={{ rows: 10 }} />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error al cargar datos"
        description={error}
        type="error"
        showIcon
        action={
          <Button onClick={fetchParseAnalytics}>
            Reintentar
          </Button>
        }
      />
    );
  }

  if (!data) {
    return (
      <Alert
        message="Sin datos disponibles"
        description="No se encontraron datos de análisis de parseo para mostrar."
        type="info"
        showIcon
      />
    );
  }

  return (
    <div style={{ padding: "0" }}>
      {/* Header con filtros y controles */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <BarChartOutlined style={{ marginRight: 8 }} />
              Análisis de Parseo
            </Title>
            <Text type="secondary">
              Comparativa entre datos originales y parseados para mejorar la precisión del sistema
            </Text>
          </div>
          <Space>
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={() => handleExport('csv')}
            >
              Exportar CSV
            </Button>
            <Button 
              icon={<DownloadOutlined />}
              onClick={() => handleExport('json')}
            >
              Exportar JSON
            </Button>
          </Space>
        </div>
      </Card>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              <UserOutlined />
              Análisis de Nombres de Usuario
            </span>
          }
          key="usernames"
        >
          {/* Estadísticas de nombres de usuario */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Total Aprobados"
                  value={data.usernameAnalysis.statistics.totalApproved}
                  prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Total Rechazados"
                  value={data.usernameAnalysis.statistics.totalRejected}
                  prefix={<CloseCircleOutlined style={{ color: "#ff4d4f" }} />}
                  valueStyle={{ color: "#ff4d4f" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Precisión Promedio"
                  value={Math.round(data.usernameAnalysis.statistics.averageAccuracy * 100)}
                  suffix="%"
                  prefix={<BarChartOutlined style={{ color: "#1890ff" }} />}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Errores Comunes"
                  value={data.usernameAnalysis.statistics.commonMistakes.length}
                  prefix={<ExclamationCircleOutlined style={{ color: "#faad14" }} />}
                  valueStyle={{ color: "#faad14" }}
                />
              </Card>
            </Col>
          </Row>

          {/* Errores comunes de nombres de usuario */}
          {data.usernameAnalysis.statistics.commonMistakes.length > 0 && (
            <Card title="Errores Más Comunes en Nombres de Usuario" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                {data.usernameAnalysis.statistics.commonMistakes.slice(0, 6).map((mistake, index) => (
                  <Col xs={24} sm={12} md={8} key={index}>
                    <Card 
                      size="small" 
                      hoverable
                      onClick={() => showMistakeDetail(mistake, 'username')}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text strong>Original:</Text> <Text code>{mistake.original}</Text><br />
                          <Text strong>Parseado:</Text> <Text code>{mistake.parsed}</Text>
                        </div>
                        <Tag color="red">{mistake.count}x</Tag>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          )}

          {/* Tabla de nombres de usuario */}
          <Card title="Detalle de Nombres de Usuario">
            <Tabs defaultActiveKey="approved">
              <TabPane tab={`Aprobados (${data.usernameAnalysis.approved.length})`} key="approved">
                <Table
                  columns={usernameColumns}
                  dataSource={data.usernameAnalysis.approved.map((item, index) => ({ ...item, key: index }))}
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </TabPane>
              <TabPane tab={`Rechazados (${data.usernameAnalysis.rejected.length})`} key="rejected">
                <Table
                  columns={usernameColumns}
                  dataSource={data.usernameAnalysis.rejected.map((item, index) => ({ ...item, key: index }))}
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </TabPane>
            </Tabs>
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <DollarOutlined />
              Análisis de Montos
            </span>
          }
          key="amounts"
        >
          {/* Estadísticas de montos */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Total Aprobados"
                  value={data.amountAnalysis.statistics.totalApproved}
                  prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Total Rechazados"
                  value={data.amountAnalysis.statistics.totalRejected}
                  prefix={<CloseCircleOutlined style={{ color: "#ff4d4f" }} />}
                  valueStyle={{ color: "#ff4d4f" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Precisión de Parseo"
                  value={Math.round(data.amountAnalysis.statistics.averageAccuracy * 100)}
                  suffix="%"
                  prefix={<BarChartOutlined style={{ color: "#1890ff" }} />}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Entradas No Numéricas"
                  value={data.amountAnalysis.statistics.nonNumericInputs}
                  prefix={<ExclamationCircleOutlined style={{ color: "#faad14" }} />}
                  valueStyle={{ color: "#faad14" }}
                />
              </Card>
            </Col>
          </Row>

          {/* Errores comunes de montos */}
          {data.amountAnalysis.statistics.commonMistakes.length > 0 && (
            <Card title="Errores Más Comunes en Montos" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                {data.amountAnalysis.statistics.commonMistakes.slice(0, 6).map((mistake, index) => (
                  <Col xs={24} sm={12} md={8} key={index}>
                    <Card 
                      size="small" 
                      hoverable
                      onClick={() => showMistakeDetail(mistake, 'amount')}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text strong>Original:</Text> <Text code>{mistake.original}</Text><br />
                          <Text strong>Parseado:</Text> <Text code>{mistake.parsed?.toString() || 'NULL'}</Text>
                        </div>
                        <Tag color="red">{mistake.count}x</Tag>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          )}

          {/* Tabla de montos */}
          <Card title="Detalle de Montos">
            <Tabs defaultActiveKey="approved">
              <TabPane tab={`Aprobados (${data.amountAnalysis.approved.length})`} key="approved">
                <Table
                  columns={amountColumns}
                  dataSource={data.amountAnalysis.approved.map((item, index) => ({ ...item, key: index }))}
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </TabPane>
              <TabPane tab={`Rechazados (${data.amountAnalysis.rejected.length})`} key="rejected">
                <Table
                  columns={amountColumns}
                  dataSource={data.amountAnalysis.rejected.map((item, index) => ({ ...item, key: index }))}
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </TabPane>
            </Tabs>
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <LineChartOutlined />
              Tendencias
            </span>
          }
          key="trends"
        >
          {/* Tendencia de análisis de nombres de usuario */}
          <Row gutter={[0, 24]} style={{ marginBottom: 24 }}>
            <Col span={24}>
                             <UsernameTrendChart 
                 dateRange={filters.dateRange.start && filters.dateRange.end ? [filters.dateRange.start, filters.dateRange.end] : null}
                 adminId="ALL"
                 loading={loading || propLoading}
               />
            </Col>
          </Row>

          {/* Tendencia de montos rechazados */}
          <Row gutter={[0, 24]}>
            <Col span={24}>
                             <RejectedAmountTrendChart 
                 dateRange={filters.dateRange.start && filters.dateRange.end ? [filters.dateRange.start, filters.dateRange.end] : null}
                 adminId="ALL"
                 loading={loading || propLoading}
               />
            </Col>
          </Row>
        </TabPane>
      </Tabs>

      {/* Modal de detalle de errores */}
      <Modal
        title="Detalle del Error"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedMistake && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Tipo">
              {selectedMistake.type === 'username' ? 'Nombre de Usuario' : 'Monto'}
            </Descriptions.Item>
            <Descriptions.Item label="Valor Original">
              <Text code>{selectedMistake.original}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Valor Parseado">
              <Text code>{selectedMistake.parsed?.toString() || 'NULL'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Frecuencia">
              <Tag color="red">{selectedMistake.count} ocurrencias</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Recomendación">
              <Alert
                message="Mejora sugerida"
                description={
                  selectedMistake.type === 'username' 
                    ? "Revisar las reglas de limpieza y normalización de nombres de usuario para mejorar la similitud."
                    : "Verificar las expresiones regulares para extracción de montos y mejorar el manejo de formatos no numéricos."
                }
                type="info"
                showIcon
              />
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default ParseAnalytics; 